import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { GetCitiesRequest, UpdateCityRequest } from '../../../protos/generated/admin.pb';
import { CustomerAssetsEndpointClient } from '../../../protos/generated/admin.pbsc';
import { AuthService } from '../../../authentication/service/auth.service';

type CityDto = { id: number; title: string; timeZoneId: string; latitude?: number; longitude?: number; disabled?: boolean };

@Injectable({ providedIn: 'root' })
export class CityService {
  constructor(
    private readonly client: CustomerAssetsEndpointClient,
    private authService: AuthService
  ) { }

  private allCities$?: Observable<CityDto[]>;
  private cityCache = new Map<number, { title: string; timeZoneId: string }>();

  private getAllCities(): Observable<CityDto[]> {
    if (!this.allCities$) {
      this.allCities$ = this.client
        .getCities(new GetCitiesRequest({ searchText: '' }), this.getMetaData)
        .pipe(
          map((res: any) => (Array.isArray(res?.cities) ? res.cities : []) as CityDto[]),
          map((cities) => {
            for (const c of cities) {
              if (c?.id != null) {
                this.cityCache.set(c.id, { title: c.title ?? '', timeZoneId: c.timeZoneId ?? '' });
              }
            }
            return cities;
          }),
          shareReplay(1)
        );
    }
    return this.allCities$;
  }

  private lookupCityMeta(cityId: number): Observable<{ title: string; timeZoneId: string } | null> {
    if (this.cityCache.has(cityId)) {
      return of(this.cityCache.get(cityId)!);
    }
    return this.getAllCities().pipe(
      map((cities) => {
        const hit = cities.find((c) => c.id === cityId);
        if (!hit) {
          return null;
        }
        const meta = { title: hit.title ?? '', timeZoneId: hit.timeZoneId ?? '' };
        this.cityCache.set(cityId, meta);
        return meta;
      })
    );
  }

  getTimeZoneIdByCityId(cityId: number | null | undefined): Observable<string | null> {
    if (cityId == null) return of(null);
    return this.lookupCityMeta(cityId).pipe(
      map((meta) => meta?.timeZoneId ?? null)
    );
  }

  getCityMetaById(cityId: number | null | undefined): Observable<{ id: number; title: string; timeZoneId: string } | null> {
    if (cityId == null) return of(null);
    return this.lookupCityMeta(cityId).pipe(
      map((meta) => (meta ? { id: cityId, title: meta.title, timeZoneId: meta.timeZoneId } : null))
    );
  }

  getCities(searchText = '') {
    return this.client.getCities(
      new GetCitiesRequest({ searchText }),
      this.getMetaData
    );
  }

  private get getMetaData(): any {
    return this.authService.getMetadata();
  }

  Updatecity(dto: {
    id: number;
    title?: string;
    timeZoneId?: string;
    latitude?: number;
    longitude?: number;
  }): Observable<any> {
    const req = new UpdateCityRequest({
      id: dto.id,
      title: dto.title ?? undefined,
      timeZoneId: dto.timeZoneId ?? undefined,
      latitude: dto.latitude,
      longitude: dto.longitude,
    });
    return this.client.updateCity(req, this.getMetaData);
  }
  
}